import React, { useState, useMemo } from "react";
import {
    Text, Table, TableBody, TableCell, TableHeader, TableHeaderCell,
    TableRow, Box, Inline, Badge, IconEdit, IconDelete,
    TextField, Stack, Button, IconAdd, Actions, Columns, Column,
    TooltipRenderer
} from "braid-design-system";
import { useTranslation } from "react-i18next";
import { PromoHedResponse } from "../../services/promotionService";

interface PromotionListTableProps {
    promotionList?: PromoHedResponse[] | null;
    onCreatePromotion?: () => void;
    onEditPromotion?: (promotion: PromoHedResponse) => void;
    onDeletePromotion?: (promotionId: number) => void;
}

const PromotionListTable: React.FC<PromotionListTableProps> = ({
    promotionList,
    onCreatePromotion,
    onEditPromotion,
    onDeletePromotion,
}) => {
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState('');

    const filteredPromotions = useMemo(() => {
        if (!promotionList) return promotionList;

        const filtered = promotionList.filter((promo) => {
            const matchesSearch = !searchTerm.trim() || (
                promo.promo_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                promo.promo_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                promo.promo_desc?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                promo.promo_type_code?.toLowerCase().includes(searchTerm.toLowerCase())
            );
            return matchesSearch;
        });

        return filtered;
    }, [promotionList, searchTerm]);

    return (
        <Stack space="large">
            <Columns space="large" collapseBelow="tablet">
                <Column width="1/3">
                    <Box display="flex" justifyContent="flexStart">
                        {onCreatePromotion && (
                            <Actions>
                                <Button
                                    variant="ghost"
                                    tone="brandAccent"
                                    icon={<IconAdd />}
                                    onClick={onCreatePromotion}
                                >
                                    {t('page.promotion.list.create', 'Create Promotion')}
                                </Button>
                            </Actions>
                        )}
                    </Box>
                </Column>
                <Column width="2/3">
                    <Box display="flex" justifyContent="flexEnd">
                        <TextField
                            placeholder={t('page.promotion.list.search_placeholder', 'Search promotions...')}
                            aria-label={t('page.promotion.list.search_aria', 'Search Promotions')}
                            onChange={(e) => setSearchTerm(e.currentTarget.value)}
                            value={searchTerm}
                        />
                    </Box>
                </Column>
            </Columns>

            <Table label={t('page.promotion.list.table_label', 'Promotions List Table')}>
                <TableHeader>
                    <TableRow>
                        <TableHeaderCell>
                            <Text>{t('page.promotion.list.headers.code', 'Code')}</Text>
                        </TableHeaderCell>
                        <TableHeaderCell>
                            <Text>{t('page.promotion.list.headers.name', 'Name')}</Text>
                        </TableHeaderCell>
                        <TableHeaderCell>
                            <Text>{t('page.promotion.list.headers.type', 'Type')}</Text>
                        </TableHeaderCell>
                        <TableHeaderCell align="center" minWidth={150}>
                            <Text>{t('page.promotion.list.headers.status', 'Status')}</Text>
                        </TableHeaderCell>
                        <TableHeaderCell>
                            <Text>{t('page.promotion.list.headers.validity', 'Validity')}</Text>
                        </TableHeaderCell>
                        <TableHeaderCell>
                            <Text>{t('page.promotion.list.headers.criteria', 'Criteria')}</Text>
                        </TableHeaderCell>
                        <TableHeaderCell align="right">
                            <Text>{t('page.promotion.list.headers.actions', 'Actions')}</Text>
                        </TableHeaderCell>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {(!filteredPromotions || filteredPromotions.length === 0) ? (
                        <TableRow>
                            <TableCell colspan={7} align="center">
                                <Text tone="secondary">
                                    {t('page.promotion.list.empty', 'No promotions found.')}
                                </Text>
                            </TableCell>
                        </TableRow>
                    ) : (
                        filteredPromotions.map((promo) => (
                            <TableRow key={promo.id}>
                                <TableCell>
                                    <Text weight="strong" tone="brandAccent">{promo.promo_code}</Text>
                                </TableCell>
                                <TableCell>
                                    <Stack space="xxsmall">
                                        <Text weight="strong">{promo.promo_name}</Text>
                                        {promo.promo_desc ? (
                                            <Text size="small" tone="secondary">{promo.promo_desc}</Text>
                                        ) : null}
                                    </Stack>
                                </TableCell>
                                <TableCell>
                                    <Badge tone="info">
                                        {promo.promo_type_code || '-'}
                                    </Badge>
                                </TableCell>
                                <TableCell align="center" minWidth={150}>
                                    <Stack space="xxsmall" align="center">
                                        {promo.promo_status === 'approved' ? (
                                            <Badge tone="positive">{t('page.promotion.list.approved', 'Approved')}</Badge>
                                        ) : promo.promo_status === 'rejected' ? (
                                            <Badge tone="critical">{t('page.promotion.list.rejected', 'Rejected')}</Badge>
                                        ) : (
                                            <Badge tone="caution">{t('page.promotion.list.pending', 'Pending Approval')}</Badge>
                                        )}
                                        {promo.promo_active ? (
                                            <Badge tone="positive">{t('page.promotion.list.active', 'Active')}</Badge>
                                        ) : (
                                            <Badge tone="neutral">{t('page.promotion.list.inactive', 'Inactive')}</Badge>
                                        )}
                                    </Stack>
                                </TableCell>
                                <TableCell>
                                    {promo.promo_datefrom && promo.promo_dateto ? (
                                        <Stack space="xsmall">
                                            <Text size="small">
                                                {promo.promo_datefrom} ~ {promo.promo_dateto}
                                            </Text>
                                            {promo.promo_timefrom || promo.promo_timeto ? (
                                                <Text size="xsmall" tone="secondary">
                                                    {promo.promo_timefrom || '00:00'} ~ {promo.promo_timeto || '23:59'}
                                                </Text>
                                            ) : null}
                                        </Stack>
                                    ) : (
                                        <Text size="small">-</Text>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <Text size="small">
                                        {String(promo.details?.length ?? 0)} {t('page.promotion.list.criteria_count', 'criteria')}
                                    </Text>
                                </TableCell>
                                <TableCell align="right">
                                    <Inline space="small">
                                        {onEditPromotion ? (
                                            <TooltipRenderer
                                                tooltip={<Text>{t('page.promotion.list.tooltip.edit', 'Update Promotion')}</Text>}
                                            >
                                                {({ triggerProps }) => (
                                                    <Box
                                                        onClick={() => onEditPromotion(promo)}
                                                        cursor="pointer"
                                                        {...triggerProps}
                                                    >
                                                        <IconEdit tone="info" />
                                                    </Box>
                                                )}
                                            </TooltipRenderer>
                                        ) : null}
                                        {onDeletePromotion && promo.id != null ? (
                                            <TooltipRenderer
                                                tooltip={<Text>{t('page.promotion.list.tooltip.delete', 'Delete Promotion')}</Text>}
                                            >
                                                {({ triggerProps }) => (
                                                    <Box
                                                        onClick={() => onDeletePromotion(promo.id!)}
                                                        cursor="pointer"
                                                        {...triggerProps}
                                                    >
                                                        <IconDelete tone="critical" />
                                                    </Box>
                                                )}
                                            </TooltipRenderer>
                                        ) : null}
                                    </Inline>
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </Stack>
    );
};

export default PromotionListTable;